import React, { useState, useEffect } from 'react';
import { Users, MapPin, Home, Heart, Globe, Cloud, Sparkles, RefreshCw, Star } from 'lucide-react';
import toast from 'react-hot-toast';
import { generateCultureData } from '../../utils/dataGeneration';
import ComparisonTable from '../../shared/ComparisonTable';
import { getCollegeName, formatArrayField as formatArray, getSchoolColor } from '../../shared/designSystem';

const CampusCultureTab = ({ colleges = [], currentUser, portfolioLocked = false, onDataGenerated, studentProfile = {}, onDeleteCollege }) => {
  const [generatingForCollege, setGeneratingForCollege] = useState(null);
  const [generatingCultureFit, setGeneratingCultureFit] = useState(false);
  const [cultureFitData, setCultureFitData] = useState(null);

  // Load cached culture fit data on mount
  useEffect(() => {
    const loadCachedData = () => {
      const cached = {};

      colleges.forEach(college => {
        const name = getCollegeName(college);
        // Check if culture_fit_analysis exists and is not an empty object
        if (college.culture_fit_analysis &&
            typeof college.culture_fit_analysis === 'object' &&
            Object.keys(college.culture_fit_analysis).length > 0) {
          cached[name] = college.culture_fit_analysis;
          console.log(`✅ [CampusCultureTab] Loaded cached data for ${name}:`, college.culture_fit_analysis);
        } else {
          console.log(`⚠️ [CampusCultureTab] No valid data for ${name}, will show generate button`);
        }
      });

      if (Object.keys(cached).length > 0) {
        setCultureFitData({ cultureFit: cached });
      }
    };

    loadCachedData();
  }, [colleges]);

  // Generate culture data for a college
  const handleGenerateCulture = async (college) => {
    setGeneratingForCollege(college.id);
    try {
      await generateCultureData(college, () => {
        if (onDataGenerated) onDataGenerated();
      });
    } finally {
      setGeneratingForCollege(null);
    }
  };

  // Generate culture data for ALL colleges at once
  const handleGenerateAllCultureData = async () => {
    setGeneratingForCollege('all');
    const collegesNeedingData = colleges.filter(c => !hasCultureData(c));

    if (collegesNeedingData.length === 0) {
      toast.success('All colleges already have culture data!');
      setGeneratingForCollege(null);
      return;
    }

    const toastId = toast.loading(`Generating culture data for ${collegesNeedingData.length} college${collegesNeedingData.length > 1 ? 's' : ''}...`);

    try {
      for (const college of collegesNeedingData) {
        await generateCultureData(college);
      }
      toast.success('Culture data generated for all colleges!', { id: toastId });
      if (onDataGenerated) onDataGenerated();
    } catch (error) {
      toast.error('Failed to generate some culture data', { id: toastId });
    } finally {
      setGeneratingForCollege(null);
    }
  };

  // Auto-generate culture data when tab loads if missing
  useEffect(() => {
    const collegesNeedingData = colleges.filter(c => !hasCultureData(c));

    if (collegesNeedingData.length > 0 && !generatingForCollege) {
      console.log(`🤖 Auto-generating culture data for ${collegesNeedingData.length} colleges`);
      handleGenerateAllCultureData();
    }
  }, [colleges.length]); // Only run when colleges array changes

  // Generate culture fit analysis
  const generateCultureFitAnalysis = async () => {
    setGeneratingCultureFit(true);
    try {
      console.log('🎨 [CampusCultureTab] Generating culture fit analysis for user:', currentUser?.id);
      console.log('🎨 [CampusCultureTab] Colleges being sent:', colleges.map(c => ({
        journey_id: c.id,
        global_id: c.global_colleges?.id,
        name: c.global_colleges?.name || c.name
      })));

      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-culture-fit-analysis',
          colleges: colleges.map(c => ({
            journey_id: c.id,  // college_journey_colleges.id (for database updates)
            id: c.global_colleges?.id,  // global_colleges.id (for reference)
            name: c.global_colleges?.name || c.name,
            campus_culture: c.global_colleges?.campus_culture,
            student_qualities: c.global_colleges?.student_qualities,
            campus_culture_details: c.global_colleges?.campus_culture_details,
            core_values: c.global_colleges?.core_values,
            student_life: c.global_colleges?.student_life,
            housing_options: c.global_colleges?.housing_options,
            popular_interest_programs: c.global_colleges?.popular_interest_programs,
            social_impact_programs: c.global_colleges?.social_impact_programs,
            ...c.global_colleges
          })),
          studentProfile,
          userId: currentUser?.id
        })
      });

      if (!response.ok) throw new Error('Failed to generate culture fit analysis');

      const { data } = await response.json();
      console.log('✅ [CampusCultureTab] Received response:', data);

      if (data?.cultureFit) {
        setCultureFitData(data);
        console.log('✅ [CampusCultureTab] Set cultureFitData with', Object.keys(data.cultureFit).length, 'colleges');
        toast.success('Culture fit analysis generated successfully');
      } else {
        console.warn('⚠️ [CampusCultureTab] Response missing cultureFit data:', data);
      }
    } catch (error) {
      console.error('Error generating culture fit analysis:', error);
      toast.error('Failed to generate culture fit analysis');
    } finally {
      setGeneratingCultureFit(false);
    }
  };

  // Check if college has culture data
  const hasCultureData = (college) => {
    const gc = college.global_colleges;
    return gc?.campus_culture || gc?.core_values || (gc?.campus_culture_details && Object.keys(gc.campus_culture_details).length > 0);
  };

  // Define campus culture metrics for ComparisonTable
  const cultureMetrics = [
    {
      label: 'Location',
      getValue: (college) => {
        const city = college.global_colleges?.city;
        const state = college.global_colleges?.state;
        const setting = college.global_colleges?.campus_setting;
        return `${city}, ${state}${setting ? ` (${setting})` : ''}`;
      }
    },
    {
      label: 'Campus Culture',
      getValue: (college) => {
        const culture = formatArray(college.global_colleges?.campus_culture);
        return culture;
      }
    },
    {
      label: 'Enrollment Size',
      getValue: (college) => college.global_colleges?.student_enrollment?.toLocaleString() || 'N/A'
    },
    {
      label: 'Student Body',
      getValue: (college) => college.global_colleges?.student_qualities?.slice(0, 3).join(', ') || 'N/A'
    },
    {
      label: 'International Students',
      getValue: (college) => {
        const percent = college.global_colleges?.percent_international;
        return percent ? `${Math.round(percent * 100)}%` : '—';
      },
      highlight: true
    },
    {
      label: 'Out-of-State Students',
      getValue: (college) => {
        const percent = college.global_colleges?.percent_out_of_state;
        return percent ? `${Math.round(percent * 100)}%` : '—';
      },
      highlight: true
    },
    {
      label: 'Housing Options',
      getValue: (college) => {
        const housing = college.global_colleges?.housing_options;
        if (!housing) return 'N/A';
        return housing.length > 100 ? housing.slice(0, 100) + '...' : housing;
      }
    },
    {
      label: 'Weather & Climate',
      getValue: (college) => college.global_colleges?.weather_climate || 'N/A'
    }
  ];

  // Create AI update handlers for each college - ALWAYS show button (generate or regenerate)
  const aiUpdateHandlers = {};
  colleges.forEach(college => {
    const collegeName = college.global_colleges?.name || college.name;
    const hasValidFitData = cultureFitData?.cultureFit?.[collegeName] &&
                           Object.keys(cultureFitData.cultureFit[collegeName]).length > 0;

    const hasCultureDataForCollege = hasCultureData(college);

    // Always show button for culture data generation/regeneration
    if (!hasCultureDataForCollege) {
      aiUpdateHandlers[college.id] = {
        tooltip: 'Generate culture data with AI',
        onClick: () => handleGenerateCulture(college),
        isLoading: generatingForCollege === college.id
      };
    } else {
      // Even if we have culture data, allow regeneration
      aiUpdateHandlers[college.id] = {
        tooltip: 'Regenerate culture data with AI',
        onClick: () => handleGenerateCulture(college),
        isLoading: generatingForCollege === college.id
      };
    }

    console.log(`🔘 [CampusCultureTab] Adding ${hasCultureDataForCollege ? 'regenerate' : 'generate'} button for ${collegeName}`);
  });

  // Check if any college is missing culture data
  const anyMissingData = colleges.some(c => !hasCultureData(c));

  return (
    <div className="space-y-8">
      {/* Campus Culture Overview */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">Campus Culture</h2>
          {anyMissingData && (
            <button
              onClick={handleGenerateAllCultureData}
              disabled={generatingForCollege === 'all'}
              className="px-4 py-2 bg-blue-500/90 text-white text-sm font-medium rounded-lg border-2 border-slate-900 hover:bg-blue-700 disabled:bg-gray-400 transition-all active:scale-95 flex items-center gap-2"
            >
              {generatingForCollege === 'all' ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Generating All Data...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Generate All Culture Data
                </>
              )}
            </button>
          )}
        </div>
        <ComparisonTable
          colleges={colleges}
          metrics={cultureMetrics}
          aiUpdateHandlers={aiUpdateHandlers}
        />
      </div>

      {/* Campus Culture Details */}
      {colleges.some(c => c.global_colleges?.campus_culture_details &&
        Object.keys(c.global_colleges.campus_culture_details).length > 0) && (
        <div className="bg-indigo-50 rounded-lg border-2 border-indigo-600 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-indigo-500 rounded-lg p-2">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xs font-semibold text-indigo-900">Campus Culture Insights</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {colleges.map((college, idx) => {
              const cultureDetails = college.global_colleges?.campus_culture_details;
              if (!cultureDetails || Object.keys(cultureDetails).length === 0) return null;

              const colors = getSchoolColor(idx);

              // Helper function to render culture detail values
              const renderValue = (value) => {
                if (Array.isArray(value)) {
                  return (
                    <ul className="list-disc list-inside space-y-1">
                      {value.map((item, idx) => (
                        <li key={idx} className="text-xs text-slate-700">{item}</li>
                      ))}
                    </ul>
                  );
                } else if (typeof value === 'object' && value !== null) {
                  return (
                    <div className="space-y-2">
                      {Object.entries(value).map(([subKey, subValue]) => (
                        <div key={subKey} className="ml-2">
                          <span className="test-sm font-semibold text-slate-800 capitalize">
                            {subKey.replace(/_/g, ' ')}:
                          </span>
                          <span className="text-xs text-slate-700 ml-2">
                            {Array.isArray(subValue) ? subValue.join(', ') : subValue}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                } else {
                  return <p className="text-xs text-slate-700">{value}</p>;
                }
              };

              return (
                <div key={college.id} className={`${colors.bgLight} rounded-lg border-2 ${colors.border} p-4`}>
                  <h4 className={`text-xs font-bold ${colors.text} mb-3`}>
                    {getCollegeName(college)}
                  </h4>
                  <div className="space-y-3">
                    {Object.entries(cultureDetails).map(([key, value]) => (
                      <div key={key}>
                        <h5 className="test-sm font-semibold text-slate-800 uppercase tracking-wide mb-1">
                          {key.replace(/_/g, ' ')}
                        </h5>
                        {renderValue(value)}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Culture Fit Analysis */}
      <div>
        {/* Always show Generate/Regenerate button at section level */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900">Culture Fit Analysis</h2>
          <button
            onClick={generateCultureFitAnalysis}
            disabled={generatingCultureFit}
            className="px-4 py-2 bg-violet-500/90 text-white font-medium rounded-lg border-2 border-slate-900 hover:bg-violet-700 disabled:bg-gray-400 transition-all active:scale-95 flex items-center gap-2"
          >
            {generatingCultureFit ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                {cultureFitData?.cultureFit ? 'Regenerating...' : 'Generating...'}
              </>
            ) : (
              <>
                {cultureFitData?.cultureFit ? <RefreshCw className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                {cultureFitData?.cultureFit ? 'Regenerate' : 'Generate Analysis'}
              </>
            )}
          </button>
        </div>

        {cultureFitData?.cultureFit ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {colleges.map((college, idx) => {
              const collegeName = college.global_colleges?.name || college.name;
              const fitData = cultureFitData.cultureFit[collegeName];

              // Validate fitData has meaningful content
              const hasValidData = fitData && (
                (fitData.stars && fitData.stars > 0) ||
                (fitData.label && fitData.label !== 'Insufficient Data for Assessment') ||
                (fitData.campusVibe && fitData.campusVibe.length > 0) ||
                (fitData.socialExtracurricular && fitData.socialExtracurricular.length > 0)
              );

              if (!hasValidData) {
                console.warn(`⚠️ [CampusCultureTab] Insufficient data for ${collegeName}:`, fitData);
                return null;
              }

              const colors = getSchoolColor(idx);

              return (
                <div
                  key={college.id}
                  className={`bg-white border-2 ${colors.border} rounded-lg p-6 space-y-4`}
                >
                  {/* Fit Score */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-slate-600 uppercase">Culture Fit</span>
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${
                              i < fitData.stars ? 'fill-current text-violet-500' : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm font-semibold text-slate-900">{fitData.label}</p>
                  </div>

                  {/* Campus Vibe */}
                  {fitData.campusVibe && fitData.campusVibe.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-600 uppercase mb-2">Campus Vibe</p>
                      <ul className="space-y-1.5">
                        {fitData.campusVibe.map((point, i) => (
                          <li key={i} className="text-sm text-slate-700 leading-relaxed flex items-start gap-2">
                            <span className="text-violet-500 mt-1">•</span>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Social & Extracurricular */}
                  {fitData.socialExtracurricular && fitData.socialExtracurricular.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-600 uppercase mb-2">Social Life</p>
                      <ul className="space-y-1.5">
                        {fitData.socialExtracurricular.map((point, i) => (
                          <li key={i} className="text-sm text-slate-700 leading-relaxed flex items-start gap-2">
                            <span className="text-violet-500 mt-1">•</span>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Lifestyle & Environment */}
                  {fitData.lifestyleEnvironment && fitData.lifestyleEnvironment.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-600 uppercase mb-2">Lifestyle</p>
                      <ul className="space-y-1.5">
                        {fitData.lifestyleEnvironment.map((point, i) => (
                          <li key={i} className="text-sm text-slate-700 leading-relaxed flex items-start gap-2">
                            <span className="text-violet-500 mt-1">•</span>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Considerations */}
                  {fitData.considerations && fitData.considerations.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-600 uppercase mb-2">Considerations</p>
                      <ul className="space-y-1.5">
                        {fitData.considerations.map((point, i) => (
                          <li key={i} className="text-sm text-slate-600 leading-relaxed flex items-start gap-2">
                            <span className="text-slate-400 mt-1">•</span>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-lg border-2 border-slate-300 p-8 text-center">
            <p className="text-sm text-slate-600 leading-relaxed max-w-md mx-auto">
              Click "Generate Analysis" above to create personalized insights about campus vibe, clubs,
              leadership opportunities, and social programs that match your interests.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampusCultureTab;